import { useState, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  EditableCollectionBinding,
  RichTextContent,
} from '@vibesites/runtime/react';

/**
 * Virtual tours with the live site's tab selector: an intro line, a row of
 * tour tabs (gold active, black inactive), and the selected tour's embed.
 */

interface VirtualTour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
}

const INTRO_DEFAULT =
  '<p>Explore your future apartment by viewing our virtual tours. Take a look around – we know you’ll love it here!</p>';

export function VirtualTourEmbed() {
  const tours = useCollectionBinding<VirtualTour>('virtualtourembed.tours', {
    collection: 'virtual_tours',
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const validTours = useMemo(
    () => (tours || []).filter((t) => t.virtual_tour_url),
    [tours],
  );

  const activeIndex = selectedIndex < validTours.length ? selectedIndex : 0;
  const activeTour = validTours[activeIndex];

  return (
    <section className="vt-embed bg-white py-8 md:py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <RichTextContent
          path="virtualtourembed.intro"
          label="Intro"
          defaultValue={INTRO_DEFAULT}
          as="div"
          className="vt-embed__intro font-body"
        />

        <EditableCollectionBinding bindingPath="virtualtourembed.tours" label="Virtual Tours">
          {validTours.length > 1 && (
            <div className="vt-embed__tabs" role="tablist" aria-label="Virtual tours">
              {validTours.map((tour, i) => {
                const active = i === activeIndex;
                return (
                  <button
                    key={`${tour.virtual_tour_url}-${i}`}
                    type="button"
                    role="tab"
                    id={`vt-tab-${i}`}
                    aria-selected={active}
                    aria-controls="vt-panel"
                    className={`vt-embed__tab font-body${active ? ' is-active' : ''}`}
                    onClick={() => setSelectedIndex(i)}
                  >
                    {tour.virtual_tour_title || `Tour ${i + 1}`}
                  </button>
                );
              })}
            </div>
          )}

          {activeTour?.virtual_tour_url ? (
            <div
              id="vt-panel"
              role={validTours.length > 1 ? 'tabpanel' : undefined}
              aria-labelledby={validTours.length > 1 ? `vt-tab-${activeIndex}` : undefined}
              className="relative w-full overflow-hidden"
              style={{ paddingBottom: '56.25%' }}
            >
              <iframe
                key={activeTour.virtual_tour_url}
                src={activeTour.virtual_tour_url}
                className="absolute inset-0 w-full h-full border-0"
                allow="fullscreen; xr-spatial-tracking"
                allowFullScreen
                title={activeTour.virtual_tour_title || 'Virtual Tour'}
              />
            </div>
          ) : (
            <div className="aspect-video w-full bg-[var(--vibe-color-surface)] flex items-center justify-center">
              <p className="text-[var(--vibe-color-text-muted)] font-body">
                No virtual tours are available for this property.
              </p>
            </div>
          )}
        </EditableCollectionBinding>
      </div>

      <style>{`
        .vt-embed__intro {
          text-align: center;
          font-size: 0.95rem;
          font-weight: 300;
          color: var(--vibe-color-text-muted);
          margin-bottom: 1.75rem;
        }
        .vt-embed__tabs {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 2px;
          margin-bottom: 0.75rem;
        }
        @media (min-width: 768px) {
          .vt-embed__tabs {
            display: flex;
          }
          .vt-embed__tab {
            flex: 1 1 0;
          }
        }
        .vt-embed__tab {
          background: #000;
          color: #fff;
          font-size: 0.95rem;
          line-height: 1.25;
          padding: 0.6rem 0.75rem;
          min-height: 2.75rem;
          border: 0;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }
        .vt-embed__tab:hover,
        .vt-embed__tab:focus-visible {
          background: color-mix(in srgb, var(--vibe-color-secondary) 70%, #000);
        }
        .vt-embed__tab:focus-visible {
          outline: 2px solid var(--vibe-color-secondary);
          outline-offset: 2px;
        }
        .vt-embed__tab.is-active {
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary, #fff);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourEmbed',
  displayName: 'Virtual Tour',
  tokens: {
    'virtualtourembed.intro': {
      label: 'Intro',
      type: 'richtext',
      defaultValue:
        '<p>Explore your future apartment by viewing our virtual tours. Take a look around – we know you’ll love it here!</p>',
    },
  },
  collectionBindings: {
    'virtualtourembed.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(VirtualTourEmbed as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default VirtualTourEmbed;
